import { ChangeEvent, MouseEvent, ReactNode, useState } from 'react';

import { Grid } from '@zoopla-eng/zds-components/grid';
import { MaskedInputField } from '@zoopla-eng/zds-components/input-field';
import { Link } from '@zoopla-eng/zds-components/link';
import { Select } from '@zoopla-eng/zds-components/select';
import { formatter as currencyFormatter } from '@zoopla/shared/currency';
import {
    BuyerType,
    BUYER_TYPE_OPTIONS,
    calculateStampDuty,
    parseBuyerType
} from './helpers/calculate-stamp-duty';
import {
    trackCalculate,
    trackPriceClick,
    trackPropertyStatus
} from './stamp-duty-calculator.tracking';

import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { CardTitle } from '@zoopla-eng/zds-components/headings';

const buyerOptions = BUYER_TYPE_OPTIONS.map<{
    id: BuyerType;
    text: string;
}>(({ buyerType, displayedText }) => ({
    id: buyerType,
    text: displayedText
}));

export type StampDutyCalculatorProps = {
    show: boolean;
    inputValue: string;
    trackingCategory: string;
    title?: string;
    body?: ReactNode;
};

export const StampDutyCalculator = ({
    show,
    inputValue: initialInputValue,
    trackingCategory,
    title,
    body
}: StampDutyCalculatorProps) => {
    const [selectedBuyerOption, setBuyerOption] = useState({
        id: '',
        text: 'Please select'
    });

    const [inputValue, setInputValue] = useState<string>(initialInputValue);

    const [result, setResult] = useState<number | undefined>(undefined);

    const buyerType = parseBuyerType(selectedBuyerOption.id);

    const onInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
        setInputValue(evt.target.value);
    };

    const onClickHandler = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();

        trackCalculate(trackingCategory);

        if (!buyerType) {
            return;
        }

        const price = calculateStampDuty(buyerType, parseInt(inputValue));
        setResult(price);
    };

    const onSelectedOptionChangeHandler = (option: {
        id: BuyerType;
        text: string;
    }) => {
        setBuyerOption(option);
        trackPropertyStatus(option.id, trackingCategory);
        setResult(undefined);
    };

    if (!show) return null;

    return (
        <Box pt="$M100" fontGroup="ladle">
            <Box pb="$M100">
                <Divider direction="horizontal" />
            </Box>
            <CardTitle>{title || 'Stamp duty calculator'}</CardTitle>
            {body || (
                <Box pt="$S100" pb="$M100">
                    We’ve updated our calculator to take into account the stamp
                    duty cut announced by the chancellor on 23 September 2022.{' '}
                    <Link
                        href={
                            '/discover/property-news/what-the-stamp-duty-cut-means-for-buyers/'
                        }
                    >
                        Read more stamp duty news
                    </Link>{' '}
                    and{' '}
                    <Link href={'/discover/buying/stamp-duty-calculator/'}>
                        how stamp duty works
                    </Link>
                    .
                </Box>
            )}

            <form>
                <Grid
                    alignItems="flex-end"
                    gridColumnGap="$S400"
                    gridRowGap="$M100"
                    gridTemplateColumns="minmax(0, 1fr)"
                    bpM100={{
                        gridTemplateColumns: '150px minmax(0, 1fr) min-content'
                    }}
                >
                    <MaskedInputField
                        type="number"
                        name="stamp-duty-price"
                        label="Price"
                        placeholder="Price"
                        value={inputValue}
                        onChange={onInputChange}
                        onFocus={() => trackPriceClick(trackingCategory)}
                        mask={(value) =>
                            value?.toString()?.length
                                ? currencyFormatter.format(Number(value))
                                : ''
                        }
                        data-testid="stamp-duty-price"
                        min="0.0"
                        step="1000"
                    />
                    <Select
                        selectedOption={selectedBuyerOption}
                        onSelectedOptionChange={(option) => {
                            onSelectedOptionChangeHandler(
                                option as { id: BuyerType; text: string }
                            );
                        }}
                        options={buyerOptions}
                        label="I am..."
                        data-testid="stamp-duty-buyer-option"
                    />
                    <Box pt="$S400" bpM100={{ pt: 0 }}>
                        <Button
                            fw
                            category="secondary"
                            onClick={onClickHandler}
                            data-testid="stamp-duty-submit"
                            disabled={!buyerType}
                            type="submit"
                        >
                            Calculate
                        </Button>
                    </Box>
                </Grid>
            </form>
            {typeof result !== 'undefined' && (
                <>
                    <Box centered py="$M300">
                        <Grid gridRowGap="$S500">
                            <Grid.Item>
                                <p>From 23rd September 2022</p>
                                <Box fontGroup="mug">
                                    <b>
                                        {currencyFormatter.format(result || 0)}
                                    </b>
                                </Box>
                            </Grid.Item>
                            {buyerType !== 'first-time-buyer' ? (
                                <Grid.Item>
                                    <p>
                                        Need to sell your home to buy this
                                        property?
                                    </p>
                                    <Link href="/home-values/">
                                        See how much your home is worth
                                    </Link>
                                </Grid.Item>
                            ) : null}
                        </Grid>
                    </Box>

                    <Box fontGroup="spoon">
                        <p>
                            This stamp duty calculator provides illustrative
                            figures for stamp duty in England and Northern
                            Ireland. We try to make sure that the information is
                            accurate at the time of publishing but the property
                            market moves fast and some information may now be
                            out of date. This calculator is not intended to
                            provide financial advice and should not be relied on
                            for any purposes. We recommend you seek advice from
                            a financial advisor in relation to any financial
                            decision.
                        </p>
                    </Box>
                </>
            )}
        </Box>
    );
};
